import React, { useEffect, useRef, useState } from "react";
import { playResult, playFanfare, feedbackOn } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { Target, Timer, RotateCcw, ArrowLeft } from "lucide-react";
import { PianoKeyboard } from "@/components/PianoKeyboard";
import { StaffViewer } from "@/components/StaffViewer";
import { playSequence } from "@/lib/audio";
import { reportScore } from "@/lib/gameScores";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G"];
const ORDER = ["C", "D", "E", "F", "G", "A", "B"];
const BLACKS = ["C#", "Db", "D#", "Eb", "F#", "Gb", "G#", "Ab", "A#", "Bb"];
const FLAT_TO_SHARP = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
const SPEED_SECS = 30;
const mobile = () => window.innerWidth < 640;
const whitesFrom = (from, count) => {
  let li = ORDER.indexOf(from[0]), o = Number(from.slice(1));
  return Array.from({ length: count }, () => { const p = `${ORDER[li]}${o}`; li += 1; if (li === 7) { li = 0; o += 1; } return p; });
};
const kb = (m) => (m === "treble" ? { from: "C4", count: mobile() ? 8 : 15 } : m === "bass" ? (mobile() ? { from: "C3", count: 8 } : { from: "C2", count: 15 }) : { from: "A3", count: mobile() ? 10 : 17 });
const nameOf = (p) => p.replace(/\d+$/, "");
const pretty = (t) => t.replace("#", "♯").replace(/^([A-G])b$/, "$1♭");
const cheer = () => playSequence(["C5", "E5", "G5", "C6"], 0.12, 0.35);
const best = (m) => Number(localStorage.getItem(`jt-ftn-${m}`) || 0);

// pool: possible targets; ok: does an answer match; keys: which keyboard keys to reveal on a mistake.
const MODES = {
  round: { label: "10-note round", tens: true, pool: () => LETTERS },
  survival: { label: "Keep going until a mistake", pool: () => LETTERS },
  black: { label: "Black key round", tens: true, pool: () => BLACKS },
  speed: { label: "30-second speed challenge", pool: () => LETTERS },
  name: { label: "Name the note", tens: true, pool: () => whitesFrom(kb("name").from, kb("name").count), byLetter: true },
  treble: { label: "Treble clef round", tens: true, pool: () => whitesFrom("C4", mobile() ? 8 : 15), exact: true },
  bass: { label: "Bass clef round", tens: true, pool: () => whitesFrom(kb("bass").from, kb("bass").count), exact: true },
  octave: { label: "Octave challenge", tens: true, pool: () => whitesFrom(kb("octave").from, kb("octave").count), exact: true },
};
const matches = (m, p, t) => (MODES[m].exact ? p === t : nameOf(p) === (FLAT_TO_SHARP[t] || t));
const pick = (m, prev) => { const p = MODES[m].pool(); let l; do { l = p[Math.floor(Math.random() * p.length)]; } while (l === prev && p.length > 1); return l; };

const Btn = ({ onClick, children, testId, primary, style }) => (
  <button onClick={onClick} data-testid={testId} style={style}
    className={`rounded-full px-4 py-2 text-sm font-semibold ${primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border hover:border-primary"}`}>{children}</button>
);

const Intro = ({ start }) => (
  <>
    <p className="mt-1 text-sm text-muted-foreground">Quick warm-ups. Pick a game — tap the key that matches, or name the key that lights up.</p>
    <div className="mt-3 flex flex-wrap gap-2">
      {Object.entries(MODES).map(([m, c], i) => <Btn key={m} primary={i === 0} onClick={() => start(m)} testId={`ftn-start-${m}`}>{c.label}</Btn>)}
    </div>
  </>
);

const Tip = ({ mode }) => {
  const text = {
    black: "Sharps go UP to the next black key on the RIGHT, Flats go DOWN to the next black key on the LEFT. See if you can find these notes.",
    speed: `Find as many notes as you can in ${SPEED_SECS} seconds. Each wrong tap loses 2 seconds.`,
    name: "A key lights up on the keyboard. Tap its letter name below.",
    treble: "Find this note on the keyboard. It has to be the exact key — the lowest key here is Middle C.",
    bass: "Find this bass clef note on the keyboard. It has to be the exact key — the highest key here is Middle C.",
    octave: "Find the exact key. The number is the octave: C4 is Middle C, and the number goes up by one at every C. So B3 is just below Middle C and E5 is in the octave above. Switch on Octave labels if you need a hint.",
  }[mode];
  return text ? <p className="mt-2 text-sm text-muted-foreground" data-testid={`ftn-tip-${mode}`}>{text}</p> : null;
};

const Result = ({ mode, over }) => (
  <p className="text-sm" data-testid="ftn-result">
    {MODES[mode].tens ? <>You got <b>{over.final}/10</b> in <b>{over.secs}s</b>.</>
      : mode === "speed" ? <>You found <b>{over.final}</b> in {SPEED_SECS} seconds.</>
      : <>You found <b>{over.final}</b> in a row.</>}
    {over.isBest && <span className="ml-2 font-semibold text-primary" data-testid="ftn-new-best">New best!</span>}
  </p>
);

const LetterPad = ({ onPick, reveal, wrong }) => (
  <div className="mt-3 flex flex-wrap gap-2" data-testid="ftn-letter-pad">
    {LETTERS.map((l) => (
      <Btn key={l} onClick={() => onPick(l)} testId={`ftn-letter-${l}`}
        style={reveal === l ? { background: "#22C55E", color: "#052e16" } : wrong === l ? { background: "#FCA5A5", color: "#450a0a" } : undefined}>{l}</Btn>
    ))}
  </div>
);

// Warm-up note games for Piano Lesson 1.
export const FindTheNote = ({ onPlayingChange }) => {
  const [mode, setMode] = useState(null);
  const [octNums, setOctNums] = useState(() => localStorage.getItem("jt-ftn-octaves") === "1");
  const toggleOct = () => setOctNums((v) => { localStorage.setItem("jt-ftn-octaves", v ? "0" : "1"); return !v; });

  const [target, setTarget] = useState(null);
  const [n, setN] = useState(0);
  const [score, setScore] = useState(0);
  const [over, setOver] = useState(null);
  const [flash, setFlash] = useState({});
  const [pad, setPad] = useState({});
  const [left, setLeft] = useState(SPEED_SECS);
  const t0 = useRef(0);
  const endAt = useRef(0);
  const scoreRef = useRef(0);
  const lock = useRef(false);
  const flashT = useRef(null);
  const waitT = useRef(null);
  const tick = useRef(null);
  const box = useRef(null);
  const clearTimers = () => { clearTimeout(flashT.current); clearTimeout(waitT.current); clearInterval(tick.current); };
  useEffect(() => clearTimers, []);
  useEffect(() => { onPlayingChange?.(!!mode); }, [mode, onPlayingChange]);
  useEffect(() => () => onPlayingChange?.(false), [onPlayingChange]);
  const cfg = mode ? MODES[mode] : null;

  const finish = (m, final) => {
    clearInterval(tick.current);
    const secs = Math.round((Date.now() - t0.current) / 1000);
    const isBest = final > best(m);
    const perfect = cfg.tens && final >= 10;
    if (isBest) { localStorage.setItem(`jt-ftn-${m}`, String(final)); if (!perfect && feedbackOn()) setTimeout(cheer, 400); }
    reportScore(`ftn-${m}`, `Piano · Find the note · ${MODES[m].label}`, m === "speed" || m === "survival" ? m : "round", final);
    if (perfect) playFanfare(0.35, `Find the note · ${MODES[m].label}`);
    setOver({ final, secs, isBest });
  };
  const showTarget = (m, t) => { setTarget(t); setFlash(MODES[m].byLetter ? { [t]: "#F59E0B" } : {}); };
  const start = (m) => {
    clearTimers(); lock.current = false; scoreRef.current = 0;
    setMode(m); setN(0); setScore(0); setOver(null); setPad({}); showTarget(m, pick(m)); t0.current = Date.now();
    if (m === "speed") {
      endAt.current = Date.now() + SPEED_SECS * 1000; setLeft(SPEED_SECS);
      tick.current = setInterval(() => {
        const s = Math.max(0, Math.ceil((endAt.current - Date.now()) / 1000));
        setLeft(s);
        if (s <= 0) { lock.current = true; finish("speed", scoreRef.current); }
      }, 200);
    }
  };
  const stop = () => { clearTimers(); lock.current = false; setMode(null); };
  const next = (s) => {
    if (cfg.tens && n + 1 >= 10) return finish(mode, s);
    setN(n + 1);
    showTarget(mode, pick(mode, target));
  };
  const keysFor = (t) => {
    const out = [];
    (box.current?.querySelectorAll('[data-testid^="key-"]') || []).forEach((el) => { const p = el.getAttribute("data-testid").slice(4); if (matches(mode, p, t)) out.push(p); });
    return out;
  };
  const answer = (ok, wrongReveal) => {
    if (!target || over || lock.current) return;
    playResult(ok);
    const s = score + (ok ? 1 : 0);
    setScore(s);
    scoreRef.current = s;
    clearTimeout(flashT.current);
    if (ok) { flashT.current = setTimeout(() => { if (!cfg.byLetter) setFlash({}); setPad({}); }, 300); return next(s); }
    if (mode === "speed") { endAt.current -= 2000; flashT.current = setTimeout(() => setFlash({}), 300); return wrongReveal(true); }
    lock.current = true;
    wrongReveal(false);
    waitT.current = setTimeout(() => { setFlash({}); setPad({}); lock.current = false; if (mode === "survival") finish(mode, s); else next(s); }, 1000);
  };
  const pressKey = (p) => {
    if (cfg.byLetter) return;
    const ok = matches(mode, p, target);
    if (ok) setFlash({ [p]: "#86EFAC" });
    answer(ok, (quick) => setFlash({ [p]: "#FCA5A5", ...(quick ? {} : Object.fromEntries(keysFor(target).map((k) => [k, "#22C55E"]))) }));
  };
  const pickLetter = (l) => {
    const right = nameOf(target);
    answer(l === right, () => setPad({ reveal: right, wrong: l }));
  };

  return (
    <div className="relative z-10 mb-24 mt-24 rounded-2xl border border-primary/40 bg-card p-4 sm:p-6" data-testid="find-the-note">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-serif text-lg"><Target size={18} className="text-primary" /> Find the note</p>
        <SoundToggle testId="ftn-sound-toggle" className="ml-auto" />
        {mode && <Btn onClick={stop} testId="ftn-back"><ArrowLeft size={13} className="mr-1 inline" /> Back</Btn>}
      </div>
      {!mode ? <Intro start={start} /> : (
        <>
          <Tip mode={mode} />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            {over ? <Result mode={mode} over={over} /> : cfg.byLetter ? (
              <p className="text-sm text-muted-foreground" data-testid="ftn-target">Which note is lit up?</p>
            ) : mode === "treble" || mode === "bass" ? (
              <div className="w-40" data-testid="ftn-target"><StaffViewer clef={mode} notes={[{ pitch: target, duration: "whole" }]} width={160} playOnClick={false} testId="ftn-staff" /></div>
            ) : (
              <p className="text-sm text-muted-foreground">Find: <span className="ml-1 font-serif text-4xl text-primary" data-testid="ftn-target">{pretty(target)}</span></p>
            )}
            <p className="flex items-center gap-3 text-sm text-muted-foreground">
              <span data-testid="ftn-score">{cfg.tens ? `${score}/${Math.min(n + (over ? 1 : 0), 10)} · note ${Math.min(n + 1, 10)} of 10` : mode === "speed" ? `Found ${score}` : `Streak ${score}`}</span>
              {mode === "speed" && <span className="font-semibold text-primary" data-testid="ftn-timer">{left}s</span>}
              <span className="inline-flex items-center gap-1"><Timer size={13} /> Best: {best(mode)}</span>
            </p>
          </div>
          <div className="mt-3" ref={box}><PianoKeyboard {...kb(mode)} onPress={pressKey} flash={flash} hideLetters cLabels={octNums} testId="ftn-keyboard" /></div>
          <button type="button" onClick={toggleOct} aria-pressed={octNums} data-testid="ftn-octave-toggle"
            className={`mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${octNums ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary"}`}>
            <span className={`h-3 w-6 rounded-full p-0.5 transition-colors ${octNums ? "bg-primary" : "bg-muted"}`}><span className={`block h-2 w-2 rounded-full bg-background transition-transform ${octNums ? "translate-x-3" : ""}`} /></span>
            Octave labels {octNums ? "on" : "off"} <span className="font-normal">(C4 = Middle C)</span>
          </button>
          {cfg.byLetter && !over && <LetterPad onPick={pickLetter} reveal={pad.reveal} wrong={pad.wrong} />}
          <div className="mt-3 flex flex-wrap gap-2">
            <Btn onClick={() => start(mode)} testId="ftn-restart"><RotateCcw size={13} className="mr-1 inline" /> Play again</Btn>
            <Btn onClick={stop} testId="ftn-change-mode">Change game</Btn>
          </div>
        </>
      )}
    </div>
  );
};
